import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { createFilterOptions } from "@mui/material";
import type { Commune } from "models";
import { communesService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { MultiAutoComplete } from "./MultiAutoComplete";
import { grey } from "@mui/material/colors";
import { multiAutoClassNames } from "utils";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: Commune) => {
    const normalizedString = stringUtil.normalize(option.libelle);
    return `${normalizedString} ${option.libelle}`;
  },
});

interface CommuneMultiAutoCompleteProps {
  value: Array<Commune>;
  onChange?: (commune: Array<Commune>) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function CommuneMultiAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<CommuneMultiAutoCompleteProps>): ReactNode {
  const [communes, setCommunes] = useState<Array<Commune>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getCommunes(): Promise<void> {
      const _communes = await communesService.getAll();
      setCommunes(_communes);
    }
    void catchErrors(getCommunes);
  }, [catchErrors]);

  return (
    <MultiAutoComplete
      value={value}
      onChange={onChange}
      options={communes}
      filterOptions={filterOptions}
      size={size}
      singleItemLabel="Commune"
      isLoading={isLoading}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      renderOption={(props, { id, libelle }, { selected }) => (
        <li {...props} key={id}>
          <div
            className={`${multiAutoClassNames.AUTOCOMPLETE_CSS_CHECKBOX} ${selected ? multiAutoClassNames.CHECKED : multiAutoClassNames.UNCHECKED}`}
          />
          {libelle} <span className="grey">({id.substring(0, 2)})</span>
        </li>
      )}
      getOptionKey={({ id }) => id}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      slotProps={{
        paper: { sx: { "&.MuiAutocomplete-paper li span.grey": { color: grey[500], ml: 0.5 } } },
      }}
    />
  );
}

export const CommuneMultiAutoCompleteWithErrorBoundary =
  withComponentErrorBoundary(CommuneMultiAutoComplete);
